import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, FileText, Lightbulb, Inbox, Mail, Pencil, X, Download, KeyRound, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useHideChatbot from "@/hooks/useHideChatbot";

import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor from "@/components/RichTextEditor";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ICON_OPTIONS = ["zap", "scissors", "home", "trending-up", "shield", "phone"];
const ARTICLE_CATEGORIES = ["Market Insight", "Investing", "Personal Finance", "Insurance", "Tax Tip"];
const TIP_CATEGORIES = ["Money Hack", "Tax Tip", "Insurance", "Investing"];

export default function AdminDashboard() {
  useHideChatbot();
  const { user, logout } = useAuth();

  const [articles, setArticles] = useState([]);
  const [tips, setTips] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  const [articleForm, setArticleForm] = useState({
    title: "",
    category: "Market Insight",
    excerpt: "",
    body: "",
    image_url: "",
    external_url: "",
    published: true,
  });
  const [tipForm, setTipForm] = useState({
    title: "",
    category: "Money Hack",
    content: "",
    icon: "zap",
  });

  // Edit modals
  const [editArticle, setEditArticle] = useState(null);
  const [editTip, setEditTip] = useState(null);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });

  const loadAll = async () => {
    try {
      const [a, t, c, n] = await Promise.all([
        api.get("/articles?published_only=false"),
        api.get("/tips"),
        api.get("/contact"),
        api.get("/newsletter"),
      ]);
      setArticles(a.data || []);
      setTips(t.data || []);
      setContacts(c.data || []);
      setSubscribers(n.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load.");
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const submitArticle = async (e) => {
    e.preventDefault();
    const bodyText = articleForm.body.replace(/<[^>]*>/g, "").trim();
    if (!bodyText) {
      toast.error("Article body can't be empty.");
      return;
    }
    try {
      await api.post("/articles", articleForm);
      toast.success("Article published.");
      setArticleForm({ title: "", category: "Market Insight", excerpt: "", body: "", image_url: "", external_url: "", published: true });
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to publish.");
    }
  };

  const saveArticleEdit = async () => {
    if (!editArticle) return;
    try {
      const { id, ...payload } = editArticle;
      await api.put(`/articles/${id}`, payload);
      toast.success("Article updated.");
      setEditArticle(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update.");
    }
  };

  const submitTip = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tips", tipForm);
      toast.success("Tip added.");
      setTipForm({ title: "", category: "Money Hack", content: "", icon: "zap" });
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed.");
    }
  };

  const saveTipEdit = async () => {
    if (!editTip) return;
    try {
      const { id, ...payload } = editTip;
      await api.put(`/tips/${id}`, payload);
      toast.success("Tip updated.");
      setEditTip(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    if (pwdForm.new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    try {
      await api.post("/auth/change-password", {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success("Password changed.");
      setShowPwdModal(false);
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to change password.");
    }
  };

  const downloadSubscribersCsv = async () => {
    try {
      const res = await api.get("/newsletter/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "newsletter-subscribers.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download.");
    }
  };

  const [refreshing, setRefreshing] = useState(false);
  const refreshNews = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/articles/refresh-news");
      if (data?.created) {
        toast.success(`Fetched: ${data.article?.title || "new article"}`);
        loadAll();
      } else {
        toast.info(data?.message || "No new article to fetch right now.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to refresh news.");
    } finally {
      setRefreshing(false);
    }
  };

  const deleteArticle = async (id) => {
    await api.delete(`/articles/${id}`);
    toast.success("Article deleted.");
    loadAll();
  };
  const deleteTip = async (id) => {
    await api.delete(`/tips/${id}`);
    toast.success("Tip deleted.");
    loadAll();
  };
  const deleteContact = async (id) => {
    await api.delete(`/contact/${id}`);
    toast.success("Submission removed.");
    loadAll();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-black sticky top-0 bg-white z-50">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="font-heading font-black text-lg tracking-tight" data-testid="dash-brand-link">
              DOM<span className="text-[#FF4500]">.</span>LAFRANCE
            </Link>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/60">/ Control Room</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.2em] text-black/60">
              {user?.email}
            </span>
            <button
              onClick={() => setShowPwdModal(true)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors flex items-center gap-2"
              data-testid="dash-change-pwd-btn"
            >
              <KeyRound className="w-3.5 h-3.5" /> Password
            </button>
            <button
              onClick={logout}
              className="font-mono text-[11px] uppercase tracking-[0.18em] border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors flex items-center gap-2"
              data-testid="dash-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 border border-black mb-10">
          {[
            { label: "Articles", value: articles.length, Icon: FileText },
            { label: "Tips", value: tips.length, Icon: Lightbulb },
            { label: "Messages", value: contacts.length, Icon: Inbox },
            { label: "Subscribers", value: subscribers.length, Icon: Mail },
          ].map((s, i) => (
            <div
              key={s.label}
              className={`p-6 ${i < 3 ? "md:border-r border-black" : ""} ${i < 2 ? "border-b md:border-b-0 border-black" : ""} ${i === 2 ? "md:border-r border-black" : ""}`}
              data-testid={`stat-${s.label.toLowerCase()}`}
            >
              <s.Icon className="w-5 h-5 text-black/50" />
              <div className="font-heading font-black text-5xl mt-4">{s.value}</div>
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/60 mt-2">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="articles" className="w-full" data-testid="dash-tabs">
          <TabsList className="border border-black rounded-none p-0 h-auto bg-white grid grid-cols-2 md:grid-cols-4 w-full md:w-auto md:inline-flex">
            <TabsTrigger value="articles" className="rounded-none border-r border-black data-[state=active]:bg-black data-[state=active]:text-white px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="tab-articles">Articles</TabsTrigger>
            <TabsTrigger value="tips" className="rounded-none border-r border-black data-[state=active]:bg-black data-[state=active]:text-white px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="tab-tips">Tips</TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-none border-r border-black data-[state=active]:bg-black data-[state=active]:text-white px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="tab-contacts">Messages</TabsTrigger>
            <TabsTrigger value="newsletter" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="tab-newsletter">Subscribers</TabsTrigger>
          </TabsList>

          {/* ARTICLES TAB */}
          <TabsContent value="articles" className="mt-8 space-y-6">
            <div className="border border-black bg-[#F5F5F5] p-4 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/60">Daily News · NewsAPI</div>
                <div className="font-heading font-semibold text-lg mt-1">
                  Auto-fetches at 08:00 (Toronto). Run it now anytime.
                </div>
              </div>
              <button
                onClick={refreshNews}
                disabled={refreshing}
                className="font-mono text-[11px] uppercase tracking-[0.18em] border border-black px-4 py-3 hover:bg-black hover:text-white transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="refresh-news-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Fetching…" : "Fetch latest news"}
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <form onSubmit={submitArticle} className="lg:col-span-5 border border-black p-6 space-y-5" data-testid="article-form">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/60">Compose · Article</div>
              <h2 className="font-heading font-bold text-2xl">New article</h2>
              <div>
                <Label className="label-eyebrow">Title</Label>
                <Input
                  className="rounded-none border-black mt-2"
                  value={articleForm.title}
                  onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                  required
                  data-testid="article-title-input"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Category</Label>
                <select
                  className="w-full border border-black rounded-none px-3 py-2 mt-2 bg-white font-mono text-sm"
                  value={articleForm.category}
                  onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                  data-testid="article-category-input"
                >
                  {ARTICLE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Excerpt</Label>
                <Textarea
                  className="rounded-none border-black mt-2"
                  rows={2}
                  value={articleForm.excerpt}
                  onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                  required
                  data-testid="article-excerpt-input"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Body</Label>
                <div className="mt-2">
                  <RichTextEditor
                    value={articleForm.body}
                    onChange={(html) => setArticleForm({ ...articleForm, body: html })}
                    testid="article-body-editor"
                  />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Image URL (optional)</Label>
                <Input
                  className="rounded-none border-black mt-2"
                  value={articleForm.image_url}
                  onChange={(e) => setArticleForm({ ...articleForm, image_url: e.target.value })}
                  data-testid="article-image-input"
                />
              </div>
              <div>
                <Label className="label-eyebrow">External link (optional — links the row to a 3rd-party article)</Label>
                <Input
                  className="rounded-none border-black mt-2"
                  value={articleForm.external_url}
                  onChange={(e) => setArticleForm({ ...articleForm, external_url: e.target.value })}
                  placeholder="https://…"
                  data-testid="article-external-url-input"
                />
              </div>
              <label className="flex items-center gap-3 font-mono text-xs">
                <input
                  type="checkbox"
                  checked={articleForm.published}
                  onChange={(e) => setArticleForm({ ...articleForm, published: e.target.checked })}
                  data-testid="article-published-checkbox"
                />
                Published immediately
              </label>
              <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" data-testid="article-submit-btn">
                <Plus className="w-4 h-4" /> Publish article
              </button>
            </form>

            <div className="lg:col-span-7 border border-black overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-black hover:bg-transparent">
                    <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Title</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Category</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Status</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {articles.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-black/40 font-mono text-xs uppercase tracking-[0.2em]">No articles yet</TableCell></TableRow>
                  )}
                  {articles.map((a) => (
                    <TableRow key={a.id} className="border-black" data-testid={`article-row-${a.id}`}>
                      <TableCell className="font-medium">{a.title}</TableCell>
                      <TableCell className="font-mono text-xs">{a.category}</TableCell>
                      <TableCell>
                        <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${a.published ? "border-[#00A86B] text-[#00A86B]" : "border-black/40 text-black/40"}`}>
                          {a.published ? "Live" : "Draft"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2 items-center">
                          <button onClick={() => setEditArticle({ ...a })} className="text-black/60 hover:text-[#FF4500]" data-testid={`edit-article-${a.id}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteArticle(a.id)} className="text-black/60 hover:text-[#FF3B30]" data-testid={`delete-article-${a.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            </div>
          </TabsContent>

          {/* TIPS TAB */}
          <TabsContent value="tips" className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <form onSubmit={submitTip} className="lg:col-span-5 border border-black p-6 space-y-5" data-testid="tip-form">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/60">Compose · Tip</div>
              <h2 className="font-heading font-bold text-2xl">New money hack</h2>
              <div>
                <Label className="label-eyebrow">Title</Label>
                <Input className="rounded-none border-black mt-2" value={tipForm.title} onChange={(e) => setTipForm({ ...tipForm, title: e.target.value })} required data-testid="tip-title-input" />
              </div>
              <div>
                <Label className="label-eyebrow">Category</Label>
                <select
                  className="w-full border border-black rounded-none px-3 py-2 mt-2 bg-white font-mono text-sm"
                  value={tipForm.category}
                  onChange={(e) => setTipForm({ ...tipForm, category: e.target.value })}
                  data-testid="tip-category-input"
                >
                  {TIP_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Icon</Label>
                <select
                  className="w-full border border-black rounded-none px-3 py-2 mt-2 bg-white font-mono text-sm"
                  value={tipForm.icon}
                  onChange={(e) => setTipForm({ ...tipForm, icon: e.target.value })}
                  data-testid="tip-icon-input"
                >
                  {ICON_OPTIONS.map((i) => <option key={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Content</Label>
                <Textarea className="rounded-none border-black mt-2" rows={4} value={tipForm.content} onChange={(e) => setTipForm({ ...tipForm, content: e.target.value })} required data-testid="tip-content-input" />
              </div>
              <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" data-testid="tip-submit-btn">
                <Plus className="w-4 h-4" /> Add tip
              </button>
            </form>

            <div className="lg:col-span-7 border border-black overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-black hover:bg-transparent">
                    <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Title</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Category</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Icon</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tips.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-black/40 font-mono text-xs uppercase tracking-[0.2em]">No tips yet</TableCell></TableRow>
                  )}
                  {tips.map((t) => (
                    <TableRow key={t.id} className="border-black" data-testid={`tip-row-${t.id}`}>
                      <TableCell className="font-medium max-w-xs truncate">{t.title}</TableCell>
                      <TableCell className="font-mono text-xs">{t.category}</TableCell>
                      <TableCell className="font-mono text-xs">{t.icon}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-2 items-center">
                          <button onClick={() => setEditTip({ ...t })} className="text-black/60 hover:text-[#FF4500]" data-testid={`edit-tip-${t.id}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteTip(t.id)} className="text-black/60 hover:text-[#FF3B30]" data-testid={`delete-tip-${t.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CONTACTS TAB */}
          <TabsContent value="contacts" className="mt-8 border border-black overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-black hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Name</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Email</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Phone</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Message</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-black/40 font-mono text-xs uppercase tracking-[0.2em]">No messages</TableCell></TableRow>
                )}
                {contacts.map((c) => (
                  <TableRow key={c.id} className="border-black" data-testid={`contact-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="font-mono text-xs">{c.email}</TableCell>
                    <TableCell className="font-mono text-xs">{c.phone || "—"}</TableCell>
                    <TableCell className="max-w-md truncate text-black/70">{c.message}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => deleteContact(c.id)} className="text-black/60 hover:text-[#FF3B30]" data-testid={`delete-contact-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          {/* NEWSLETTER TAB */}
          <TabsContent value="newsletter" className="mt-8 space-y-4">
            <div className="flex justify-end">
              <button
                onClick={downloadSubscribersCsv}
                className="font-mono text-[11px] uppercase tracking-[0.18em] border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors flex items-center gap-2"
                data-testid="newsletter-export-btn"
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
            <div className="border border-black overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-black hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Email</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em] text-black">Subscribed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.length === 0 && (
                  <TableRow><TableCell colSpan={2} className="text-center py-8 text-black/40 font-mono text-xs uppercase tracking-[0.2em]">No subscribers yet</TableCell></TableRow>
                )}
                {subscribers.map((s) => (
                  <TableRow key={s.id} className="border-black" data-testid={`sub-row-${s.id}`}>
                    <TableCell className="font-medium">{s.email}</TableCell>
                    <TableCell className="font-mono text-xs">{new Date(s.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* EDIT ARTICLE MODAL */}
      <Dialog open={!!editArticle} onOpenChange={(o) => !o && setEditArticle(null)}>
        <DialogContent className="rounded-none border-black max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl flex items-center justify-between">
              Edit article
              <button onClick={() => setEditArticle(null)} className="text-black/60 hover:text-black"><X className="w-5 h-5" /></button>
            </DialogTitle>
          </DialogHeader>
          {editArticle && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="label-eyebrow">Title</Label>
                <Input className="rounded-none border-black mt-2" value={editArticle.title} onChange={(e) => setEditArticle({ ...editArticle, title: e.target.value })} data-testid="edit-article-title" />
              </div>
              <div>
                <Label className="label-eyebrow">Category</Label>
                <select
                  className="w-full border border-black rounded-none px-3 py-2 mt-2 bg-white font-mono text-sm"
                  value={editArticle.category}
                  onChange={(e) => setEditArticle({ ...editArticle, category: e.target.value })}
                >
                  {ARTICLE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Excerpt</Label>
                <Textarea className="rounded-none border-black mt-2" rows={2} value={editArticle.excerpt} onChange={(e) => setEditArticle({ ...editArticle, excerpt: e.target.value })} />
              </div>
              <div>
                <Label className="label-eyebrow">Body</Label>
                <div className="mt-2">
                  <RichTextEditor
                    value={editArticle.body || ""}
                    onChange={(html) => setEditArticle({ ...editArticle, body: html })}
                    testid="edit-article-body-editor"
                  />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Image URL</Label>
                <Input className="rounded-none border-black mt-2" value={editArticle.image_url || ""} onChange={(e) => setEditArticle({ ...editArticle, image_url: e.target.value })} />
              </div>
              <div>
                <Label className="label-eyebrow">External link</Label>
                <Input className="rounded-none border-black mt-2" value={editArticle.external_url || ""} onChange={(e) => setEditArticle({ ...editArticle, external_url: e.target.value })} />
              </div>
              <label className="flex items-center gap-3 font-mono text-xs">
                <input type="checkbox" checked={!!editArticle.published} onChange={(e) => setEditArticle({ ...editArticle, published: e.target.checked })} />
                Published
              </label>
              <div className="flex gap-3 pt-4 border-t border-black/10">
                <button onClick={saveArticleEdit} className="btn-primary flex-1" data-testid="save-article-edit-btn">Save changes</button>
                <button onClick={() => setEditArticle(null)} className="btn-secondary flex-1">Cancel</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* EDIT TIP MODAL */}
      <Dialog open={!!editTip} onOpenChange={(o) => !o && setEditTip(null)}>
        <DialogContent className="rounded-none border-black max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl flex items-center justify-between">
              Edit tip
              <button onClick={() => setEditTip(null)} className="text-black/60 hover:text-black"><X className="w-5 h-5" /></button>
            </DialogTitle>
          </DialogHeader>
          {editTip && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="label-eyebrow">Title</Label>
                <Input className="rounded-none border-black mt-2" value={editTip.title} onChange={(e) => setEditTip({ ...editTip, title: e.target.value })} data-testid="edit-tip-title" />
              </div>
              <div>
                <Label className="label-eyebrow">Category</Label>
                <select
                  className="w-full border border-black rounded-none px-3 py-2 mt-2 bg-white font-mono text-sm"
                  value={editTip.category}
                  onChange={(e) => setEditTip({ ...editTip, category: e.target.value })}
                >
                  {TIP_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Icon</Label>
                <select
                  className="w-full border border-black rounded-none px-3 py-2 mt-2 bg-white font-mono text-sm"
                  value={editTip.icon || "zap"}
                  onChange={(e) => setEditTip({ ...editTip, icon: e.target.value })}
                >
                  {ICON_OPTIONS.map((i) => <option key={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Content</Label>
                <Textarea className="rounded-none border-black mt-2" rows={4} value={editTip.content} onChange={(e) => setEditTip({ ...editTip, content: e.target.value })} />
              </div>
              <div className="flex gap-3 pt-4 border-t border-black/10">
                <button onClick={saveTipEdit} className="btn-primary flex-1" data-testid="save-tip-edit-btn">Save changes</button>
                <button onClick={() => setEditTip(null)} className="btn-secondary flex-1">Cancel</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CHANGE PASSWORD MODAL */}
      <Dialog open={showPwdModal} onOpenChange={setShowPwdModal}>
        <DialogContent className="rounded-none border-black max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl flex items-center justify-between">
              Change password
              <button onClick={() => setShowPwdModal(false)} className="text-black/60 hover:text-black"><X className="w-5 h-5" /></button>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-4 mt-4" data-testid="change-pwd-form">
            <div>
              <Label className="label-eyebrow">Current password</Label>
              <Input
                type="password"
                className="rounded-none border-black mt-2"
                value={pwdForm.current_password}
                onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                required
                data-testid="current-pwd-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow">New password (min 8 chars)</Label>
              <Input
                type="password"
                className="rounded-none border-black mt-2"
                value={pwdForm.new_password}
                onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                required
                minLength={8}
                data-testid="new-pwd-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Confirm new password</Label>
              <Input
                type="password"
                className="rounded-none border-black mt-2"
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                required
                data-testid="confirm-pwd-input"
              />
            </div>
            <div className="flex gap-3 pt-4 border-t border-black/10">
              <button type="submit" className="btn-primary flex-1" data-testid="save-pwd-btn">Update password</button>
              <button type="button" onClick={() => setShowPwdModal(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
